<template>
	<div class="noshow-container">
		<Loading v-if="isLoading" />
		<template v-else>
			<!-- Header -->
			<div class="page-header">
				<div class="header-icon">
					<v-icon size="28" color="#c62828">mdi-account-cancel</v-icon>
				</div>
				<h1 class="page-title">{{ $t("noShow.txtTitle") }}</h1>
				<p class="page-subtitle">{{ $t("noShow.txtSubtitle") || 'Documenta la evidencia del No Show' }}</p>
			</div>

			<!-- Instructions Card -->
			<div class="content-section">
				<div class="info-card">
					<div class="card-header">
						<v-icon size="18" color="#6200ea">mdi-information-outline</v-icon>
						<span>{{ $t("noShow.txtInstructions") || 'Instrucciones' }}</span>
					</div>
					<ul class="instructions-list">
						<li>
							<span class="instruction-number">1</span>
							<span>{{ $t("global.components.noshow.txtPhoto1") }}</span>
						</li>
						<li>
							<span class="instruction-number">2</span>
							<span>{{ $t("global.components.noshow.txtPhoto2") }}</span>
						</li>
						<li>
							<span class="instruction-number">3</span>
							<span>{{ $t("global.components.noshow.txtPhoto3") }}</span>
						</li>
					</ul>
				</div>

				<!-- Capture Actions -->
				<div class="capture-section">
					<button class="capture-btn" @click.prevent="showCamera">
						<v-icon size="24" color="#6200ea">mdi-camera</v-icon>
						<span>{{ $t("noShow.takePhoto") }}</span>
					</button>

					<div class="upload-wrapper">
						<v-file-input
							multiple
							accept="image/*"
							show-size
							prepend-icon=""
							:label="$t('noShow.uploadImages') || 'Subir imágenes'"
							@change="handleFileUpload"
							outlined
							dense
							hide-details
							class="modern-file-input"
						>
							<template v-slot:prepend-inner>
								<v-icon color="#6200ea">mdi-upload</v-icon>
							</template>
						</v-file-input>
					</div>
				</div>

				<!-- Photos Grid -->
				<div v-if="photos.length > 0" class="photos-section">
					<div class="section-label">
						<v-icon size="16" color="#666">mdi-image-multiple</v-icon>
						<span>{{ $t("noShow.txtPhotos") || 'Fotos' }} ({{ photos.length }}/3)</span>
					</div>
					<div class="photos-grid">
						<div
							v-for="(photo, index) in photos"
							:key="index"
							class="photo-item"
						>
							<v-img :src="photo" aspect-ratio="1" class="photo-img">
								<template v-slot:placeholder>
									<div class="photo-loading">
										<v-progress-circular
											indeterminate
											color="#6200ea"
											size="24"
											width="2"
										></v-progress-circular>
									</div>
								</template>
							</v-img>
							<button class="delete-btn" @click.prevent="removeImage(index)">
								<v-icon size="16" color="white">mdi-close</v-icon>
							</button>
						</div>
					</div>
				</div>

				<!-- Empty State -->
				<div v-else class="photos-empty">
					<v-icon size="40" color="#ccc">mdi-image-off-outline</v-icon>
					<span>{{ $t("noShow.txtNoPhotos") || 'Sin fotos agregadas' }}</span>
					<span class="empty-hint">{{ $t("noShow.txtMinPhotos") || 'Mínimo 3 fotos requeridas' }}</span>
				</div>

				<!-- Notes -->
				<div class="notes-section">
					<label class="input-label">{{ $t('booking.notes') }}</label>
					<v-textarea
						v-model="notes"
						auto-grow
						rows="3"
						:placeholder="$t('noShow.notesPlaceholder') || 'Agrega notas adicionales (opcional)'"
						outlined
						hide-details
						class="modern-textarea"
					></v-textarea>
				</div>
			</div>

			<!-- Submit Button -->
			<div class="action-container">
				<div class="progress-indicator">
					<div
						class="progress-dot"
						:class="{ 'dot-active': photos.length >= 1 }"
					></div>
					<div
						class="progress-dot"
						:class="{ 'dot-active': photos.length >= 2 }"
					></div>
					<div
						class="progress-dot"
						:class="{ 'dot-active': photos.length >= 3 }"
					></div>
				</div>
				<button
					class="submit-btn"
					:class="{ 'btn-disabled': !validNoShow || sendNoShow }"
					:disabled="!validNoShow || sendNoShow"
					@click.prevent="noshow"
				>
					<v-icon v-if="!sendNoShow" size="20" color="white" class="mr-2">mdi-account-cancel</v-icon>
					<v-progress-circular
						v-else
						indeterminate
						color="white"
						size="20"
						width="2"
						class="mr-2"
					></v-progress-circular>
					{{ $t("noShow.btnSubmit") || 'Reportar No Show' }}
				</button>
			</div>
		</template>

		<!-- Camera Component -->
		<camera
			v-model="openCamera"
			:title="$t('noShow.takePhoto')"
			@capture="capture"
		/>

		<!-- Loading Dialog -->
		<v-dialog v-model="dialogLoading" persistent width="200" content-class="loading-dialog">
			<div class="loading-container">
				<v-progress-circular
					indeterminate
					color="#6200ea"
					size="40"
					width="3"
				></v-progress-circular>
				<span class="loading-text">{{ $t("home.waiting") }}...</span>
			</div>
		</v-dialog>
	</div>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";

const Loading = () =>
	import(/* webpackChunkName: "Loading" */ "@/components/loading.vue");
const camera = () =>
	import(/* webpackChunkName: "Camera" */ "@/components/camera.vue");

export default {
	name: "NoShow",
	components: { Loading, camera },
	data() {
		return {
			isLoading: true,
			position: null,
			booking: {},
			form: false,
			notes: "",
			agreement: false,
			openCamera: false,
			photos: [],
			sendNoShow: false,
			modelReference: "transport-services",
			rules: {
				required: (v) => !!v || "This field is required",
			},
		};
	},
	methods: {
		...mapActions(['initGpsTracking']),
		...mapMutations("services", {
			addGpcLocation: "addGpcLocation",
		}),
		...mapActions("ui", {
			disabledNavigation: "disabledNavigation",
			enableddNavigation: "enableddNavigation",
			openQRReaderOnlyEvent: "openQRReaderOnlyEvent",
			activeBackBtn: "activeBackBtn",
			showProgress: "showProgress",
			hideProgress: "hideProgress",
			resetQR: "resetQR",
			openSnackbar: "openSnackbar",
			showAlert: "showAlert",
		}),
		capture(photo) {
			this.photos.push(photo);
		},
		removeImage(index) {
			this.photos.splice(index, 1);
		},
		showCamera() {
			this.openCamera = true;
		},
		fetchModel() {
			this.$axios
				.get(`driver/transport-services/${this.$route.params.id}`)
				.then((res) => {
					this.booking = res.data;
					this.isLoading = false;
					this.initMap();
				})
				.catch(() => {
					//
				});
		},
		async getLocation() {
			await this.initGpsTracking();
			return this.geolocationData;
		},
		async saveGpcLocationStatus(status) {
			const position = await this.getLocation();
			this.addGpcLocation({
				status,
				latitude: position.latitude,
				longitude: position.longitude,
				speed: position.speed,
			});
		},
		async initMap() {
			const position = await this.getLocation();
			this.position = {
				lat: position.latitude,
				lng: position.longitude,
				speed: position.speed,
			};
		},
		async noshow() {
			this.showProgress();
			this.sendNoShow = true;

			try {
				const f = this.convertFiles(this.photos);
				const position = await this.getLocation();

				// Save GPS location
				this.addGpcLocation({
					status: "noShow",
					latitude: position.latitude,
					longitude: position.longitude,
					speed: position.speed,
				});

				// Upload images
				for (let i = 0; i < f.length; i++) {
					await this.uploadImage({
						input: f[i],
						key: "changeStatusEvidence[]",
					});
				}

				const url = `driver/transport-services/${this.booking.id}/change-tracking-status`;

				await this.$axios.put(url, {
					status: "noShow",
					tracking: {
						latitude: position.latitude,
						longitude: position.longitude,
						velocityKmh: position.speed || 0,
					},
				});

				// Success - navigate to Home
				this.$router.push({ name: "Home" });

				// Show success alert
				this.showAlert({
					title: this.$t("noShow.txtAlertSuccessTitle"),
					message: this.$t("noShow.txtUploadSuccess"),
					type: 'success'
				});
			} catch (error) {
				console.error("Error in noshow:", error);
				this.showAlert({
					title: this.$t("noShow.txtAlertErrorTitle"),
					message: this.$t("noShow.txtUploadError"),
					type: 'error'
				});
			} finally {
				this.hideProgress();
				this.sendNoShow = false;
			}
		},

		/* ============================================================
		 * BACKGROUND UPLOAD - Desactivado temporalmente
		 * Para activar: reemplazar noshow() con la versión comentada
		 * ============================================================
		async noshowBackground() {
			this.sendNoShow = true;

			try {
				const f = this.convertFiles(this.photos);
				const position = await this.getLocation();
				const bookingId = this.booking.id;

				this.addGpcLocation({
					status: "noShow",
					latitude: position.latitude,
					longitude: position.longitude,
					speed: position.speed,
				});

				this.openSnackbar(this.$t("noShow.txtUploading"));
				this.$router.push({ name: "Home" });

				this.uploadInBackground(f, position, bookingId);
			} catch (error) {
				console.error("Error preparing noshow:", error);
				this.openSnackbar(this.$t("booking.txtErrorChangeStatus"));
				this.sendNoShow = false;
			}
		},
		async uploadInBackground(files, position, bookingId) {
			try {
				for (let i = 0; i < files.length; i++) {
					await this.uploadImage({
						input: files[i],
						key: "changeStatusEvidence[]",
						bookingId: bookingId,
					});
				}

				const url = `driver/transport-services/${bookingId}/change-tracking-status`;

				await this.$axios.put(url, {
					status: "noShow",
					tracking: {
						latitude: position.latitude,
						longitude: position.longitude,
						velocityKmh: position.speed || 0,
					},
				});

				this.showAlert({
					title: this.$t("noShow.txtAlertSuccessTitle"),
					message: this.$t("noShow.txtUploadSuccess"),
					type: 'success'
				});
			} catch (error) {
				console.error("Error in background upload:", error);
				this.showAlert({
					title: this.$t("noShow.txtAlertErrorTitle"),
					message: this.$t("noShow.txtUploadError"),
					type: 'error'
				});
			}
		},
		============================================================ */
		convertFiles(photos) {
			const files = [];
			photos.forEach((elm) => {
				var block = elm.split(";");
				var contentType = block[0].split(":")[1];
				var realData = block[1].split(",")[1];
				files.push(this.b64toBlob(realData, contentType));
			});
			return files;
		},
		uploadImage(fileState) {
			return new Promise((rr, ff) => {
				let formData = new FormData();
				formData.append("file", fileState.input);
				formData.append("keyName", fileState.key);

				const bookingId = fileState.bookingId || this.booking.id;

				this.$axios
					.post(`files/${this.modelReference}/${bookingId}`, formData, {
						headers: { "Content-Type": "multipart/form-data" },
					})
					.then(() => {
						rr();
					})
					.catch(() => {
						ff();
					});
			});
		},
		b64toBlob(b64Data, contentType, sliceSize) {
			contentType = contentType || "";
			sliceSize = sliceSize || 512;

			var byteCharacters = atob(b64Data);
			var byteArrays = [];

			for (
				var offset = 0;
				offset < byteCharacters.length;
				offset += sliceSize
			) {
				var slice = byteCharacters.slice(offset, offset + sliceSize);

				var byteNumbers = new Array(slice.length);
				for (var i = 0; i < slice.length; i++) {
					byteNumbers[i] = slice.charCodeAt(i);
				}

				var byteArray = new Uint8Array(byteNumbers);
				byteArrays.push(byteArray);
			}

			var blob = new Blob(byteArrays, { type: contentType });
			return blob;
		},
		handleFileUpload(files) {
			Array.from(files).forEach((file) => {
				const reader = new FileReader();
				reader.onload = (e) => {
					this.photos.push(e.target.result);
				};
				reader.readAsDataURL(file);
			});
		}
	},
	computed: {
		...mapState(['geolocationData']),
		...mapState("ui", {
			dialogLoading: (state) => state.core.dialogLoading,
		}),
		positionValid() {
			return this.position !== null;
		},
		validNoShow() {
			return this.photos.length >= 3;
		},
	},
	mounted() {
		this.activeBackBtn();
		this.disabledNavigation();
		this.fetchModel();
	},
};
</script>

<style scoped>
.noshow-container {
	min-height: 100vh;
	background: #fafafa;
	padding-bottom: 100px;
}

.page-header {
	background: #fff;
	padding: 24px 16px;
	text-align: center;
	border-bottom: 1px solid #eee;
}

.header-icon {
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: #ffebee;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 14px;
}

.page-title {
	font-size: 20px;
	font-weight: 600;
	color: #1a1a1a;
	margin: 0 0 4px;
}

.page-subtitle {
	font-size: 13px;
	color: #888;
	margin: 0;
}

.content-section {
	padding: 16px;
}

.info-card {
	background: #fff;
	border-radius: 12px;
	padding: 16px;
	margin-bottom: 16px;
}

.card-header {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	font-weight: 600;
	color: #666;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 14px;
	padding-bottom: 10px;
	border-bottom: 1px solid #f0f0f0;
}

.instructions-list {
	list-style: none;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.instructions-list li {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	font-size: 13px;
	color: #555;
	line-height: 1.5;
}

.instruction-number {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: #ede7f6;
	color: #6200ea;
	font-size: 12px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.capture-section {
	display: flex;
	gap: 12px;
	margin-bottom: 16px;
}

.capture-btn {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 20px 16px;
	background: #fff;
	border: 2px dashed #6200ea;
	border-radius: 12px;
	cursor: pointer;
	transition: all 0.15s ease;
}

.capture-btn span {
	font-size: 13px;
	font-weight: 600;
	color: #6200ea;
}

.capture-btn:active {
	transform: scale(0.98);
	background: #faf8ff;
}

.upload-wrapper {
	flex: 1;
}

.modern-file-input >>> .v-input__slot {
	border-radius: 12px !important;
	min-height: 80px !important;
	border: 2px dashed #e0e0e0 !important;
}

.section-label {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	font-weight: 600;
	color: #666;
	margin-bottom: 12px;
}

.photos-section {
	background: #fff;
	border-radius: 12px;
	padding: 16px;
	margin-bottom: 16px;
}

.photos-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 10px;
}

.photo-item {
	position: relative;
	border-radius: 10px;
	overflow: hidden;
}

.photo-img {
	border-radius: 10px;
}

.photo-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	background: #f5f5f5;
}

.delete-btn {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: rgba(198, 40, 40, 0.9);
	border: none;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.15s ease;
}

.delete-btn:active {
	transform: scale(0.9);
}

.photos-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 32px 16px;
	background: #fff;
	border-radius: 12px;
	margin-bottom: 16px;
	gap: 8px;
}

.photos-empty span {
	font-size: 14px;
	color: #999;
}

.empty-hint {
	font-size: 12px !important;
	color: #bbb !important;
}

.notes-section {
	background: #fff;
	border-radius: 12px;
	padding: 16px;
}

.input-label {
	display: block;
	font-size: 13px;
	font-weight: 500;
	color: #555;
	margin-bottom: 8px;
}

.modern-textarea >>> .v-input__slot {
	border-radius: 10px !important;
}

.modern-textarea >>> textarea {
	font-size: 14px;
}

.action-container {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 12px 16px 24px;
	background: #fff;
	border-top: 1px solid #eee;
}

.progress-indicator {
	display: flex;
	justify-content: center;
	gap: 8px;
	margin-bottom: 12px;
}

.progress-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #e0e0e0;
	transition: all 0.2s ease;
}

.dot-active {
	background: #4caf50;
}

.submit-btn {
	width: 100%;
	padding: 14px;
	border: none;
	border-radius: 10px;
	background: #c62828;
	color: #fff;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.15s ease;
	display: flex;
	align-items: center;
	justify-content: center;
}

.submit-btn:active {
	transform: scale(0.98);
	background: #b71c1c;
}

.btn-disabled {
	background: #ccc;
	cursor: not-allowed;
}

.btn-disabled:active {
	transform: none;
	background: #ccc;
}

.loading-container {
	background: #fff;
	border-radius: 12px;
	padding: 28px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
}

.loading-text {
	font-size: 14px;
	color: #666;
}
</style>

<style>
.loading-dialog {
	border-radius: 12px !important;
}
</style>
